<script setup lang="ts">
import { computed, ref, Ref, watch } from 'vue';
import MultiChartV3 from '@/components/charts/multi-chart-v3.vue';
import RexModalV3 from '@/rex-components/RexModalV3.vue';
import AnomalySideBar from '@/components/charts/utils/AnomalySideBar.vue';
// import GraphCustomLegends from '@/components/Common/GraphCustomLegends.vue';
import AnomalyGraphFooter from '@/components/charts/utils/AnomalyGraphFooter.vue';
import {
  GraphDataNamesEnum,
  GraphTypesEnum,
  AnomalyDataObjectType,
} from '@/components/Common/graph.types';
import { useI18n } from 'vue-i18n';
import {
  anomalyChartOptionsValues,
  ANNOTATION_RANGE,
  anomalyChartLegends,
  respFormatter,
} from '@/components/Common/graph.config';
import { useGraphData } from '@/components/Common/useGraphData';
import { useThemeToken } from '@/utils/theme/useThemeToken';
import { useStoreData } from '@/components/Common/useStoreData';
import {
  GetChartOptionsParamType,
  GraphTypes,
  ParamsType,
  Aggregation,
} from '@/components/Common/graph.types';
// import { LegendInfoType } from '@/components/Common/graph.types';
import { notificationError } from '@/utils/notification';
import { cloneDeep } from 'lodash';
import {
  graphTooltipKeys,
  anomalyInfoObject,
} from '@/components/charts/utils/utils.config';
import { GraphTooltipKeysType } from '@/components/charts/utils/utils.types';
import ForecastToggle from '@/components/IntegrationUtils/ForecastToggle.vue';
import ForecastDatePicker from '@/components/IntegrationUtils/ForecastDatePicker.vue';
import RexSkeletonV3 from '@/rex-components/RexSkeletonV3.vue';
import RexTooltipV3 from '@/rex-components/RexTooltipV3.vue';
import AnomalyGraphHeader from '@/components/charts/utils/AnomalyGraphHeader.vue';
import { useGraphActions } from '@/components/Common/useGraphActions';
import { RexConfigProvider } from '@/rex-components/rex.comp.config';

interface Props {
  title?: string;
  queryName: string;
  params?: ParamsType;
  chartOptionsData?: GetChartOptionsParamType;
  graphType?: GraphTypes;
  metricName: string;
  serviceName?: string;
  axisLabels: { x: string; y: string };
  aggregation?: Aggregation;
}

interface SelectedAnomalyType {
  keys: string[];
  info: AnomalyDataObjectType;
}

const emit = defineEmits(['onClose']);
const props = withDefaults(defineProps<Props>(), {
  params: () => ({
    condition: {
      name: '',
      entity: {},
    },
    duration: {},
  }),
  chartOptionsData: () => anomalyChartOptionsValues,
  graphType: GraphTypesEnum.FORECAST_WITH_ANOMALY,
  serviceName: '',
  aggregation: () => ({ req: false }),
});

const { token } = useThemeToken();
const { t } = useI18n();
const { setIsForecastModalEnable, isForecastModalEnable, currentSideNav } =
  useStoreData();

const selectedAnomaly: Ref<SelectedAnomalyType> = ref({
  keys: [],
  info: anomalyInfoObject,
});
const multiChart: any = ref(null);

const legendList = ref(cloneDeep(anomalyChartLegends));
const tooltipInfo: Ref<GraphTooltipKeysType> = ref(graphTooltipKeys);
const anomalyInfo: Ref<AnomalyDataObjectType> = ref(anomalyInfoObject);
const graphType = ref(props.graphType);
const chartOptions = ref(props.chartOptionsData);
const aggregation = ref(props.aggregation);
const { data, isLoading, error, options, anomalyList, makegraphQLCall } =
  useGraphData(
    props.queryName,
    props.params,
    chartOptions,
    graphType,
    props.axisLabels,
    aggregation,
    // legendList,
  );

watch([error], () => {
  if (error) {
    notificationError(error.value?.message || t('errWhileFetchingData'));
  }
});

// function clearLegendSelection() {
//   legendList.value?.forEach((obj) => (obj.isActive = true));
// }

function onSelectAnomaly(selectedItem, itemInfo) {
  // clearLegendSelection();
  selectedAnomaly.value = { keys: selectedItem, info: itemInfo };
  anomalyInfo.value = anomalyList.value[selectedItem[0].split('-')[0]];
  const timeStamp = new Date(itemInfo.date).getTime();
  chartOptions.value = {
    ...chartOptions.value,
    annotationRange: [
      timeStamp - ANNOTATION_RANGE,
      timeStamp + ANNOTATION_RANGE,
    ],
  };
}

// const getActiveCount = computed(() =>
//   legendList.value?.reduce((acc, obj) => {
//     if (obj.isActive) acc++;
//     return acc;
//   }, 0),
// );

// function onLegendClick(obj: LegendInfoType, index: number) {
//   if (getActiveCount.value > 1 || !obj.isActive) {
//     multiChart.value?.chart?.toggleSeries?.(obj.label);
//     legendList.value[index].isActive = !obj.isActive;
//   }
// }

function onMouseMoveGraph(seriesArr) {
  let j = 0;
  legendList.value?.forEach((obj, i) => {
    tooltipInfo.value[GraphDataNamesEnum.TIMESTAMPS] = seriesArr[0]?.x;
    if (obj.isActive && obj.key in graphTooltipKeys) {
      if (obj.key === GraphDataNamesEnum.UPPER_THRESHOLD) {
        tooltipInfo.value[GraphDataNamesEnum.LOWER_THRESHOLD] =
          seriesArr[j]?.y?.[0];
        tooltipInfo.value[GraphDataNamesEnum.UPPER_THRESHOLD] =
          seriesArr[j]?.y?.[1];
      } else {
        tooltipInfo.value[obj.key] = seriesArr[j]?.y;
      }
    }
    if (obj.isActive) j++;
  });
}

function handleClose() {
  emit('onClose');
  Object.keys(graphTooltipKeys).forEach(
    (key) => (graphTooltipKeys[key] = null),
  );
}

function onForecastChange(data) {
  setIsForecastModalEnable(data);
}

function onDurationChange(duration) {
  selectedAnomaly.value.keys = [];
  selectedAnomaly.value.info = anomalyInfoObject;
  chartOptions.value.annotationRange = [];
  let params = {
    ...props.params,
    duration: duration,
  };
  makegraphQLCall(props.queryName, params, respFormatter(graphType.value));
  // clearLegendSelection();
}

const { handleZoomIn, handleZoomOut, handleReset } = useGraphActions(
  multiChart,
  graphType,
);
</script>

<template>
  <div>
    <RexConfigProvider
      :theme="{
        components: {
          Modal: {
            colorBgElevated: '#181a1d',
          },
        },
      }"
    >
      <RexModalV3
        :show="true"
        @onClose="handleClose"
        width="85vw"
        :enableFooter="true"
        :bodyStyle="{}"
        :headerStyle="{ border: 'none' }"
      >
        <template #header>
          <div class="anly-modal-header">
            <div class="anmly-title-header">
              <span>
                {{ title }}
                <BaseIcon
                  name="starFilled"
                  width="1em"
                  height="1em"
                  class="anmly-header-icon-suffix"
                />
              </span>
              <span class="anmly-header-separator"> . </span>
              <RexTooltipV3 :title="serviceName" class="anmly-header-text">
                <div class="anmly-header-ellipsis-text">
                  {{ serviceName }}
                </div>
              </RexTooltipV3>
              <!-- <span class="anmly-header-separator"> . </span>
              <span class="anmly-header-label">
                {{ currentSideNav.name }}
              </span> -->
            </div>
            <div class="anly-header-actions">
              <ForecastDatePicker @onDurationChange="onDurationChange" />
              <ForecastToggle
                :showForecastToggle="true"
                :isForcastEnabled="isForecastModalEnable"
                @change="onForecastChange"
              />
            </div>
          </div>
        </template>
        <template #body>
          <div class="anly-side-bar-graph-box">
            <AnomalySideBar
              class="anly-side-bar"
              :selectedItems="selectedAnomaly.keys"
              :anomalyList="anomalyList"
              @onSelect="onSelectAnomaly"
            />
            <RexSkeletonV3
              :loading="isLoading"
              :paragraph="{ rows: 8 }"
              class="anly-modal-graph-loading-comp"
              v-if="isLoading"
            >
            </RexSkeletonV3>
            <div class="anly-graph-comp" v-else>
              <AnomalyGraphHeader
                :selectedAnomalyInfo="selectedAnomaly.info"
                :anomaliesList="anomalyList"
                :handleZoomOut="handleZoomOut"
                :handleZoomIn="handleZoomIn"
                :handleReset="handleReset"
                :actionsIdPrefix="title || 'graph-action'"
              />
              <div class="anly-multi-graph">
                <MultiChartV3
                  :series="data"
                  :chartOptions="options"
                  ref="multiChart"
                  @onMouseMove="onMouseMoveGraph"
                />
              </div>
              <!-- <GraphCustomLegends
                :legendList="legendList"
                @onLegendClick="onLegendClick"
              /> -->
              <AnomalyGraphFooter
                :tooltipInfoData="tooltipInfo"
                :anomalyInfo="anomalyInfo"
                :isAnomalySelected="selectedAnomaly?.keys?.length > 0"
              />
            </div>
          </div>
        </template>
        <template #footer>
          <div></div>
        </template>
      </RexModalV3>
    </RexConfigProvider>
  </div>
</template>

<style lang="scss" scoped>
.anly-side-bar-graph-box {
  display: flex;
  height: 70vh;
}
.anly-side-bar {
  flex: 1;
}
.anly-graph-comp {
  border: 0.01rem solid $border-gray;
  flex: 5;
}
.anly-multi-graph {
  height: calc(100% - 21.5rem);
  margin-bottom: 2rem;
  margin-left: 0.75rem;
}
.anly-modal-header {
  display: flex;
  justify-content: space-between;
  margin-right: 2rem;
}
:deep(.rex-service-sel) {
  height: 2.5rem;
}
:deep(.selected) {
  height: 2.5rem;
}
.anly-header-actions {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
}
.anmly-header-icon-suffix {
  @include svg-fill($color-active-blue);
  vertical-align: super;
}
.anmly-header-separator {
  margin: 0 0.8rem;
}
.anmly-title-header {
  display: flex;
}
.anmly-header-text {
  @include flex-center-vert;
}
.anmly-header-ellipsis-text {
  @include truncate(15rem);
}
.anmly-header-label {
  @include flex-center-vert;
  background-color: $color-black;
  padding: 0 0.8rem;
  border-radius: 0.3rem;
}
.anly-modal-graph-loading-comp {
  margin: 1rem;
  flex: 5;
}
</style>
